/** @format */

import { useState } from "react";
import { Spin, Table, Tag, Button } from "antd";
import Column from "antd/lib/table/Column";
import { IAccountPhoneNumbers, ProvisioningStatus } from "../MainContent.types";
import {
	formatDateTime,
	capitalizeWord,
} from "../../../services/UtilityService";
import DeletePhoneNumberModal from "./DeletePhoneNumberModal";

interface IPhoneNumbersOverviewViewProps {
	accountPhoneNumbers: IAccountPhoneNumbers[];
	isLoading: boolean;
	setRemountHelper: React.Dispatch<React.SetStateAction<boolean>>;
}

const PhoneNumbersOverviewView: React.FC<IPhoneNumbersOverviewViewProps> = ({
	accountPhoneNumbers,
	isLoading,
	setRemountHelper,
}) => {
	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	/* const [selectedRows, setSelectedRows] = useState<IAccountPhoneNumbers[]>([]); */

	function totalNumbersInfo() {
		let total = 0;
		let toll = 0;
		let tollFree = 0;
		for (const number in accountPhoneNumbers) {
			total += 1;
			if (accountPhoneNumbers[number].phoneNumber.isTollFree === true) {
				tollFree += 1;
			} else {
				toll += 1;
			}
		}
		return `Total: ${total} |  Toll Free: ${tollFree} |  Local: ${toll}`;
	}

	const rowSelection = {
		selectedRowKeys,
		onChange: (
			selectedRowKeys: React.Key[],
			selectedRows: IAccountPhoneNumbers[]
		) => {
			setSelectedRowKeys(selectedRowKeys as string[]);
			/* setSelectedRows(selectedRows); */
		},
		getCheckboxProps: (record: IAccountPhoneNumbers) => ({
			disabled: record.phoneNumber.provisioningStatus === ProvisioningStatus.Assigned,
			style: record.phoneNumber.provisioningStatus === ProvisioningStatus.Assigned
				? { display: 'none' }
				: {},
		}),
		onSelect: (record: IAccountPhoneNumbers, selected: boolean, selectedRows: IAccountPhoneNumbers[]) => {
			if (record.phoneNumber.provisioningStatus === ProvisioningStatus.Assigned) {
				return false;
			}
		},
		onSelectAll: (selected: boolean, selectedRows: IAccountPhoneNumbers[], changeRows: IAccountPhoneNumbers[]) => {
			// Filter out assigned numbers from select all operation
			const selectableRows = changeRows.filter(
				row => row.phoneNumber.provisioningStatus !== ProvisioningStatus.Assigned
			);

			const selectableKeys = selectableRows.map(row => row.phoneNumber.e164Number);

			if (selected) {
				// Add only selectable rows to selection
				setSelectedRowKeys(prev => [...new Set([...prev, ...selectableKeys])]);
			} else {
				// Remove selectable rows from selection
				setSelectedRowKeys(prev => prev.filter(key => !selectableKeys.includes(key)));
			}

			return false; // Prevent default behavior
		},
	};

	return (
		<Spin spinning={isLoading} tip="Fetching accounts data...">
			{openDeleteModal ? (
				<DeletePhoneNumberModal
					setRemountHelper={setRemountHelper}
					selectedRowKeys={selectedRowKeys}
					setSelectedRowKeys={setSelectedRowKeys}
					accountPhoneNumbers={accountPhoneNumbers}
					setOpenDeleteModal={setOpenDeleteModal}
				/>
			) : null}

			<div style={{ margin: "20px" }}>
				<div style={{ marginTop: "-20px", marginBottom: "10px" }}>
					{totalNumbersInfo()}
					<Button
						onClick={() => setOpenDeleteModal(true)}
						danger
						style={{ marginLeft: "15px" }}
						disabled={selectedRowKeys.length === 0}>
						Delete
					</Button>
				</div>
				<Table
					rowSelection={{
						type: "checkbox",
						...rowSelection,
					}}
					dataSource={accountPhoneNumbers}
					size="small"
					rowKey={(record: IAccountPhoneNumbers) =>
						record.phoneNumber.e164Number
					}
					pagination={{
						defaultPageSize: 10,
						showSizeChanger: true,
						pageSizeOptions: ["10", "50", "100", "250"],
					}}>
					<Column
						title="Number"
						dataIndex="phoneNumber"
						key="phoneNumber"
						sorter={(
							a: { phoneNumber: { e164Number: string } },
							b: { phoneNumber: { e164Number: string } }
						) => {
							a.phoneNumber.e164Number = a.phoneNumber.e164Number || "";
							b.phoneNumber.e164Number = b.phoneNumber.e164Number || "";
							return a.phoneNumber.e164Number.localeCompare(
								b.phoneNumber.e164Number
							);
						}}
						render={(number) => number.e164Number}
					/>
					<Column
						title="Name"
						dataIndex="webName"
						key="webName"
						sorter={(a: { webName: string }, b: { webName: string }) => {
							a.webName = a.webName || "";
							b.webName = b.webName || "";
							return a.webName.localeCompare(b.webName);
						}}
					/>
					<Column title="Account ID" dataIndex="accountId" key="accountId" />
					<Column
						title="Toll Free"
						dataIndex="phoneNumber"
						key="tollFree"
						align="center"
						filters={[
							{ text: "Yes", value: "true" },
							{ text: "No", value: "false" },
						]}
						onFilter={(
							value: string | number | boolean,
							data: IAccountPhoneNumbers
						) => {
							if (data.phoneNumber) {
								return data.phoneNumber.isTollFree.toString() === value;
							} else return false;
						}}
						render={(number) => {
							return (
								<Tag color={number.isTollFree === true ? "green" : "red"}>
									{number.isTollFree === true ? "Yes" : "No"}
								</Tag>
							);
						}}
					/>
					<Column
						title="State"
						dataIndex="phoneNumber"
						key="state"
						sorter={(
							a: { phoneNumber: { state: string } },
							b: { phoneNumber: { state: string } }
						) => {
							a.phoneNumber.state = a.phoneNumber.state || "";
							b.phoneNumber.state = b.phoneNumber.state || "";
							return a.phoneNumber.state.localeCompare(b.phoneNumber.state);
						}}
						render={(number) => number.state}
					/>
					<Column
						title="Setup Cost"
						dataIndex="phoneNumber"
						key="setupCost"
						align="center"
						render={(phoneNumber) =>
							phoneNumber.setupCost
								? "$" + phoneNumber.setupCost.toFixed(2)
								: "$0"
						}
					/>
					<Column
						title="MRC"
						dataIndex="phoneNumber"
						key="mrc"
						align="center"
						render={(phoneNumber) =>
							phoneNumber.mrc ? "$" + phoneNumber.mrc.toFixed(2) : "$0"
						}
					/>
					<Column
						title="Purchase Date"
						dataIndex="phoneNumber"
						key="purchaseDate"
						sorter={(
							a: { phoneNumber: { purchaseDate: string } },
							b: { phoneNumber: { purchaseDate: string } }
						) => {
							a.phoneNumber.purchaseDate = a.phoneNumber.purchaseDate || "";
							b.phoneNumber.purchaseDate = b.phoneNumber.purchaseDate || "";
							return a.phoneNumber.purchaseDate.localeCompare(
								b.phoneNumber.purchaseDate
							);
						}}
						render={(phoneNumber) =>
							phoneNumber.purchaseDate
								? formatDateTime(phoneNumber.purchaseDate)
								: null
						}
					/>
					<Column
						title="Last Billed Date"
						dataIndex="phoneNumber"
						key="lastBilledDate"
						render={(phoneNumber) =>
							phoneNumber.lastBilledDate
								? formatDateTime(phoneNumber.lastBilledDate)
								: null
						}
					/>
					<Column
						title="Status"
						dataIndex="phoneNumber"
						key="provisioningStatus"
						align="center"
						filters={[
							{ text: "Provisioning", value: ProvisioningStatus.Provisioning },
							{ text: "Completed", value: ProvisioningStatus.Completed },
							{ text: "Error", value: ProvisioningStatus.Error },
							{ text: "Deleted", value: ProvisioningStatus.Deleted },
							{ text: "Assigned", value: ProvisioningStatus.Assigned },
						]}
						onFilter={(
							value: string | number | boolean,
							data: IAccountPhoneNumbers
						) => {
							if (typeof value === "string" && data.phoneNumber) {
								return data.phoneNumber.provisioningStatus.indexOf(value) === 0;
							} else return false;
						}}
						render={(phoneNumber) => {
							const renderTagColor = () => {
								if (
									phoneNumber.provisioningStatus ===
									ProvisioningStatus.Provisioning
								)
									return "orange";
								else if (
									phoneNumber.provisioningStatus ===
									ProvisioningStatus.Completed
								)
									return "green";
								else if (
									phoneNumber.provisioningStatus === ProvisioningStatus.Error
								)
									return "red";
								else if (
									phoneNumber.provisioningStatus === ProvisioningStatus.Deleted
								)
									return "gold";
							};

							return (
								<Tag color={renderTagColor()}>
									{capitalizeWord(
										phoneNumber.provisioningStatus
											? phoneNumber.provisioningStatus
											: null
									)}
								</Tag>
							);
						}}
					/>
					<Column
						title="Last MRC Date"
						dataIndex="phoneNumber"
						key="lastMRCDate"
						render={(phoneNumber) =>
							phoneNumber.lastMRCDate
								? formatDateTime(phoneNumber.lastMRCDate)
								: null
						}
					/>
				</Table>
			</div>
		</Spin>
	);
};

export default PhoneNumbersOverviewView;
